import React from "react"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { SingleFloat } from "../../components/single/SingleFloat"
import { CustomAudioFrequencyConfig } from "../../configuration/components/CustomAudioFrequencyConfig"
import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { AudioFrequencyMetricRecord } from "./AudioFrequencyMetricRecord"

export class CustomAudioFrequencyMetricRecord extends AudioFrequencyMetricRecord {
  get hasVersionedConfig() {
    return true
  }

  private _getFrequencyThreshold(): number {
    return this.metric_metadata?.frequency_threshold ?? 300
  }

  private _getDirection(): "above" | "below" {
    return this.metric_metadata?.direction ?? "above"
  }

  private _getDisplayCutoff(): number {
    return this.metric_metadata?.percent_above_threshold ?? 50
  }

  private _getThresholdAssessment(value: number): {
    label: string
    color: string
  } {
    const cutoff = this._getDisplayCutoff()
    if (value >= cutoff) {
      return { label: "Passes Filter", color: "success" }
    }
    if (value >= cutoff / 2) {
      return { label: "Near Cutoff", color: "warning" }
    }
    return { label: "Fails Filter", color: "danger" }
  }

  getHeaderValue(mo: RunMetricOutput[]): string | null {
    const valid = mo.filter(
      (m) => m.float_value != null && Number.isFinite(m.float_value),
    )
    if (valid.length === 0) {
      return null
    }
    const average =
      valid.map((m) => m.float_value as number).reduce((a, b) => a + b, 0) /
      valid.length
    return this._getThresholdAssessment(average).label
  }

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode | null {
    return <SingleFloat mo={mo} units="%" formatFn={(v) => v.toFixed(1)} />
  }

  // Skip AudioFrequencyMetricRecord's "Robotic/Flat" chip — use NumericalMetricRecord's value chip instead.
  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    return NumericalMetricRecord.prototype._renderSimulationCard.call(this, mo)
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const value = mo.float_value || 0
    const freqThreshold = this._getFrequencyThreshold()
    const direction = this._getDirection()
    const cutoff = this._getDisplayCutoff()
    return (
      <div className="space-y-4 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold">
              {value.toFixed(2)}% of speech segments {direction} {freqThreshold}
              Hz
            </span>
          </div>
        </div>

        <div>
          <h3 className="font-semibold">Interpretation</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-coval-success-text" />
              <span>
                <strong>&ge;{cutoff}%:</strong> Passes Filter - Most voiced
                segments are {direction} {freqThreshold}Hz
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-coval-warning-text" />
              <span>
                <strong>
                  {Math.round(cutoff / 2)}-{cutoff}%:
                </strong>{" "}
                Near Cutoff - Some segments are {direction} {freqThreshold}Hz
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-coval-danger-text" />
              <span>
                <strong>&lt;{Math.round(cutoff / 2)}%:</strong> Fails Filter -
                Few segments are {direction} {freqThreshold}Hz
              </span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // TODO: Version tracking should use runtime_metadata once structured runtime metadata is implemented.
  // frequency_threshold and percent_above_threshold should move to runtime_metadata.
  isMetricOutputCurrentVersion(_metricOutput: {
    result_json?: Record<string, unknown> | null
  }): boolean {
    return true
  }

  getVersionDiff(_metricOutput: {
    result_json?: Record<string, unknown> | null
  }): Array<{ field: string; current: string; previous: string }> {
    return []
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
  ): React.ReactNode {
    return (
      <CustomAudioFrequencyConfig
        metricRecordData={metricRecordData}
        onChange={onChange}
      />
    )
  }
}
